/**
 * @voltxampere/web — Live voltage labels (Sprint 18 D1, A2).
 *
 * Generates `probe-overlay` SceneNode instances from the active
 * `Circuit` + the latest DC result. The canvas merges these
 * labels into the scene's selection layer the same way it does
 * tool ghosts.
 *
 * The mockup (zenit-platform.html line 1682) shows live
 * voltages rendered next to the relevant node — e.g. "+8.245V"
 * brass-hot for V_out. The store renders one label per non-
 * ground net, anchored at the world position of the net's first
 * terminal (with the component's rotation applied). Ground nets
 * are skipped — KCL pins them at 0 V and the schematic ground
 * symbol already communicates the same fact.
 *
 * When the engine has not converged yet (no DC result) the store
 * still emits labels but with the em-dash fallback that
 * `drawProbeOverlay` already honors — Kural 9 mandates honesty
 * about "no value yet" rather than a stale zero.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, type ReadonlySignal } from "@preact/signals-core";
import type { Circuit, ComponentInstance, Rotation } from "@voltxampere/schema";
import { formatVoltage, PROBE_NO_VALUE, type SceneNode } from "@voltxampere/ui/canvas";

import { currentCircuit } from "../store/circuit.js";
import { currentDCResult } from "../store/dc-result.js";
import { transientLive } from "../store/transient-runner.js";

/**
 * Apply a 90°-multiple rotation around the origin in canvas
 * coordinates. Canvas Y axis grows downward, so positive
 * rotation values produce a visually-clockwise rotation; this
 * matches `drawComponent`'s `ctx.rotate((rotation * Math.PI)/180)`.
 */
function rotatePoint(pt: { x: number; y: number }, rotation: Rotation): { x: number; y: number } {
  switch (rotation) {
    case 0:
      return { x: pt.x, y: pt.y };
    case 90:
      return { x: -pt.y, y: pt.x };
    case 180:
      return { x: -pt.x, y: -pt.y };
    case 270:
      return { x: pt.y, y: -pt.x };
  }
}

/**
 * Compute the world-space anchor point for a terminal on a placed
 * component instance. Mirrors the transform `drawComponent` applies
 * (translate → rotate → flip).
 */
function terminalWorldPosition(
  instance: ComponentInstance,
  localPosition: { x: number; y: number },
): { x: number; y: number } {
  const rotated = rotatePoint(localPosition, instance.rotation);
  const flipX = instance.flipped ? -1 : 1;
  return {
    x: instance.position.x + flipX * rotated.x,
    y: instance.position.y + rotated.y,
  };
}

/**
 * For each non-ground net, locate the world position of its first
 * terminal (across all components in the circuit) so the canvas
 * can park a label near a component pin rather than floating in
 * empty space.
 *
 * Returns an empty `Map` when the circuit has no nets, no
 * components, or every net is ground — the caller treats that as
 * "nothing to overlay".
 */
export function netAnchorPoints(circuit: Circuit): Map<string, { x: number; y: number }> {
  const groundNetIds = new Set(circuit.nets.filter((n) => n.isGround).map((n) => n.id));
  const out = new Map<string, { x: number; y: number }>();
  for (const component of circuit.components) {
    for (const terminal of component.terminals) {
      if (groundNetIds.has(terminal.netId)) continue;
      if (out.has(terminal.netId)) continue;
      out.set(terminal.netId, terminalWorldPosition(component, terminal.position));
    }
  }
  return out;
}

/**
 * Build the per-net label map the canvas renders. Combines the
 * pre-formatted DC voltage (or em-dash fallback) with the world
 * anchor of each net.
 */
export function voltageLabelNodes(
  circuit: Circuit | null,
  dcResult: { nodeVoltages: Readonly<Record<string, number>> } | null,
  live: boolean,
): readonly SceneNode[] {
  if (circuit === null) return [];
  const anchors = netAnchorPoints(circuit);
  if (anchors.size === 0) return [];
  const intensity: "static" | "live" = live ? "live" : "static";
  const out: SceneNode[] = [];
  for (const [netId, position] of anchors) {
    const voltage = dcResult?.nodeVoltages[netId];
    const label = voltage === undefined ? PROBE_NO_VALUE : formatVoltage(voltage);
    out.push({
      kind: "probe-overlay",
      id: `voltage-label-${netId}`,
      position,
      label,
      intensity,
    });
  }
  return out;
}

/**
 * Reactive scene-node list. Rebuilds whenever `currentCircuit`,
 * `currentDCResult`, or `transientLive` change.
 */
export const voltageLabels: ReadonlySignal<readonly SceneNode[]> = computed(() => {
  return voltageLabelNodes(currentCircuit.value, currentDCResult.value, transientLive.value);
});
